/**
 * Wer darf was — als reine Funktionen, damit es pruefbar ist.
 *
 * Zwei Regeln aus Bauplan §15 stehen hier, und beide sind der Grund, warum
 * diese Datei ohne React auskommt: eine Regel, die nur im JSX einer Seite
 * lebt, laesst sich nicht testen, und genau bei diesen beiden waere „sieht
 * richtig aus" zu wenig.
 *
 * 1. **Rote Freigabe** (§15.1 S3): Nur `admin`, und nur mit Rueckrufnummer
 *    und Namen des Gespraechspartners. Die Oberflaeche haelt dieselbe Regel
 *    wie die API — nicht, weil der Server ihr nicht traute, sondern damit der
 *    Mensch vor dem Absenden sieht, was fehlt, statt hinterher eine 422.
 * 2. **Pruefliste** (§15.2): Sie zeigt Funde aus **persoenlichen** Postfaechern.
 *    Wer sie ungefiltert saehe, laese die Privatpost der Geschaeftsfuehrer.
 *    Der Server filtert; hier wird zusaetzlich gefiltert. Zwei Schloesser an
 *    einer Tuer sind hier angemessen — es ist fremde Post.
 */

import type { ReviewItemResponse, User, Warnung } from '@/types';

/** Die Rolle, wie sie `/session` liefert. `admin` ist das einzige Wort, das zaehlt. */
export function istAdmin(user: User | null | undefined): boolean {
  return user?.role === 'admin';
}

// ---------------------------------------------------------------------------
// Freigabe
// ---------------------------------------------------------------------------

/**
 * Die Rueckrufangaben, so wie sie im Dialog getippt werden.
 *
 * Getrennt vom Schema-Typ `Freigabe`, weil hier noch Strings mit Leerzeichen
 * stehen — der Nutzlast-Typ kennt nur fertige Werte oder `null`.
 */
export interface Rueckruf {
  nummer: string;
  gesprochenMit: string;
}

/** Mindestlaengen wie in `IbanBestaetigung` — dieselbe Angabe, dieselbe Huerde. */
const NUMMER_MIN = 5;
const NAME_MIN = 2;

/** Hat der Beleg eine rote Warnung? (Dieselbe Frage wie `hatRoteWarnung`.) */
function rotDarunter(warnungen: Warnung[]): boolean {
  return warnungen.some((w) => w.stufe === 'rot');
}

/**
 * Darf dieser Mensch diesen Beleg ueberhaupt freigeben?
 *
 * Gelb darf jeder. Rot nur ein Admin — auch dann erst mit Rueckruf, das
 * entscheidet `freigabeBereit`.
 */
export function darfFreigeben(user: User | null | undefined, warnungen: Warnung[]): boolean {
  return rotDarunter(warnungen) ? istAdmin(user) : true;
}

/**
 * Sind die Pflichtfelder gefuellt?
 *
 * Bei Gelb gibt es keine Pflichtfelder. Bei Rot sind es beide — einzeln
 * genommen taugen sie nichts: eine Nummer ohne Namen ist eine halbe Angabe,
 * und die API weist sie ohnehin ab (`_rueckruf_paarweise`).
 */
export function freigabeBereit(
  user: User | null | undefined,
  warnungen: Warnung[],
  rueckruf: Rueckruf,
): boolean {
  if (!rotDarunter(warnungen)) return true;
  if (!istAdmin(user)) return false;
  return (
    rueckruf.nummer.trim().length >= NUMMER_MIN &&
    rueckruf.gesprochenMit.trim().length >= NAME_MIN
  );
}

/** Was im Dialog noch fehlt, in einem Satz — oder `null`, wenn nichts fehlt. */
export function freigabeFehlt(
  user: User | null | undefined,
  warnungen: Warnung[],
  rueckruf: Rueckruf,
): string | null {
  if (!rotDarunter(warnungen)) return null;
  if (!istAdmin(user)) {
    return 'Eine rote Warnung gibt nur ein Admin frei. Bitte an Henri oder Anton weiterreichen.';
  }
  const nummerFehlt = rueckruf.nummer.trim().length < NUMMER_MIN;
  const nameFehlt = rueckruf.gesprochenMit.trim().length < NAME_MIN;
  if (nummerFehlt && nameFehlt) return 'Rückrufnummer und Gesprächspartner fehlen.';
  if (nummerFehlt) return 'Die Rückrufnummer fehlt.';
  if (nameFehlt) return 'Der Gesprächspartner fehlt.';
  return null;
}

// ---------------------------------------------------------------------------
// Prüfliste
// ---------------------------------------------------------------------------

/**
 * Darf dieser Mensch diesen Fund sehen?
 *
 * Der Inhaber des Postfachs und `admin` — sonst niemand. `mailbox` ist die
 * Mailadresse des durchsuchten Postfachs; verglichen wird ohne
 * Gross-Kleinschreibung, weil Gmail sie nicht unterscheidet.
 */
export function darfEintragSehen(
  user: User | null | undefined,
  eintrag: Pick<ReviewItemResponse, 'mailbox'>,
): boolean {
  if (!user) return false;
  if (istAdmin(user)) return true;
  return eintrag.mailbox.trim().toLowerCase() === user.email.trim().toLowerCase();
}

/** Dieselbe Regel auf eine ganze Liste. */
export function sichtbareEintraege(
  user: User | null | undefined,
  eintraege: ReviewItemResponse[],
): ReviewItemResponse[] {
  return eintraege.filter((e) => darfEintragSehen(user, e));
}

/**
 * Entscheiden darf, wer sehen darf.
 *
 * Getrennte Funktion, obwohl sie heute dasselbe sagt: Sehen und Entscheiden
 * sind zwei Rechte, und wenn eines davon spaeter enger wird, soll man nicht
 * beide gleichzeitig anfassen muessen.
 */
export function darfEntscheiden(
  user: User | null | undefined,
  eintrag: Pick<ReviewItemResponse, 'mailbox'>,
): boolean {
  return darfEintragSehen(user, eintrag);
}
